// helper functions for patient stats

import { PatientStartDateDto } from "../../../api-client";
import { startOfWeek, addWeeks, startOfMonth, addMonths, isEqual, isBefore, startOfYear, addYears, format } from 'date-fns';


// structure of the resulting flattened data
interface FlattenedPatientData {
    locationId: string,
    locationName: string,
    patientStartDates: Date,
}

// function to flatten data (since PatientStartDates is array of string dates)
const flattenData = (patientStats: PatientStartDateDto[]): FlattenedPatientData[] => {
    return patientStats.flatMap(entry => 
        entry.patientStartDates.map(date => ({
            locationId: entry.locationId,
            locationName: entry.locationName,
            patientStartDates: new Date(date),
        }))
    )
}


// structure of grouped data (group by location)
interface GroupedData {
    [locationId: string]: FlattenedPatientData[]
}

interface CumulativeData {
    locationId: string, 
    locationName: string, 
    date: Date, 
    cumulativeSum: number
}

// function to calculate cumulative count
export const calculateCumulativePatients = (patientStats: PatientStartDateDto[], binType: 'day' | 'week' | 'month' | 'year'): CumulativeData[] => {
    // Array to store the results
    const cumulativeData: CumulativeData[] = [];
    
    // flatten data first
    const flattenedData: FlattenedPatientData[] = flattenData(patientStats);

    // Group by locationId 
    const groupedData: GroupedData = Object.groupBy(flattenedData, ({ locationId }) => locationId) as GroupedData;

    // Loop through each location group
    Object.keys(groupedData).forEach(locationId => {
        // Sort by date, ascending order
        groupedData[locationId].sort((a, b) => a.patientStartDates.getTime() - b.patientStartDates.getTime());

        const firstEntryDate = new Date(groupedData[locationId][0].patientStartDates);
        const lastEntryDate = new Date(groupedData[locationId][groupedData[locationId].length - 1].patientStartDates);
        // console.log("patients earliest date: ", firstEntryDate);
        // console.log("patients latest date: ", lastEntryDate);

        let cumulativeSum = 0; 

        if (binType === 'day') {
            const uniqueDates = new Set(groupedData[locationId].map((entry) => format(entry.patientStartDates, "yyyy-MM-dd")));

            uniqueDates.forEach((date) => {
                // count patients in current date/bin
                const patientsInBin = groupedData[locationId].filter((entry) => 
                    format(entry.patientStartDates, "yyyy-MM-dd") === date
                ).length;

                cumulativeSum += patientsInBin;

                cumulativeData.push({
                    locationId: locationId,
                    locationName: groupedData[locationId][0].locationName,
                    date: new Date(date),
                    cumulativeSum: cumulativeSum
                });
            });
        } else {
            let binStart: Date;
            let nextBinStart: Date;

            if (binType === 'week') {
                binStart = startOfWeek(firstEntryDate, { weekStartsOn: 1 });
            } else if (binType === 'month') {
                binStart = startOfMonth(firstEntryDate);
            } else {
                binStart = startOfYear(firstEntryDate);
            }

            while(isBefore(binStart, lastEntryDate)) {
                if (binType === 'week') {
                    nextBinStart = addWeeks(binStart, 1);
                } else if (binType === 'month') {
                    nextBinStart = addMonths(binStart, 1);
                } else {
                    nextBinStart = addYears(binStart, 1);
                }

                // count patients in current bin
                const patientsInBin = groupedData[locationId].filter(entry => 
                    entry.patientStartDates >= binStart && entry.patientStartDates < nextBinStart
                ).length;

                cumulativeSum += patientsInBin;

                cumulativeData.push({
                    locationId: locationId,
                    locationName: groupedData[locationId][0].locationName,
                    date: binStart,
                    cumulativeSum: cumulativeSum
                });

                binStart = nextBinStart;
            }
        }
    });
    return cumulativeData;
}


interface CountData {
    locationId: string, 
    locationName: string, 
    date: Date, 
    newPatients: number
}

// function to calculate number of new patients
export const calculateNewPatients = (patientStats: PatientStartDateDto[], binType: 'day' | 'week' | 'month' | 'year'): CountData[] => {
    // Array to store the results
    const countData: CountData[] = [];

    // flatten data first
    const flattenedData: FlattenedPatientData[] = flattenData(patientStats);

    // Group by locationId 
    const groupedData: GroupedData = Object.groupBy(flattenedData, ({ locationId }) => locationId) as GroupedData;

    // Loop through each location group
    Object.keys(groupedData).forEach(locationId => {
        // Sort by date, ascending order
        groupedData[locationId].sort((a, b) => a.patientStartDates.getTime() - b.patientStartDates.getTime());

        const firstEntryDate = new Date(groupedData[locationId][0].patientStartDates);
        const lastEntryDate = new Date(groupedData[locationId][groupedData[locationId].length - 1].patientStartDates);

        if (binType === 'day') {
            const uniqueDates = new Set(groupedData[locationId].map((entry) => format(entry.patientStartDates, "yyyy-MM-dd")));

            uniqueDates.forEach((date) => {
                // count patients in current bin
                const patientsInBin = groupedData[locationId].filter(entry => 
                    format(entry.patientStartDates, "yyyy-MM-dd") === date
                ).length;

                countData.push({
                    locationId: locationId,
                    locationName: groupedData[locationId][0].locationName,
                    date: new Date(date),
                    newPatients: patientsInBin
                });
            });
        } else {
            let binStart: Date;
            let nextBinStart: Date;

            if (binType === 'week') {
                binStart = startOfWeek(firstEntryDate, { weekStartsOn: 1 });
            } else if (binType === 'month') {
                binStart = startOfMonth(firstEntryDate);
            } else {
                binStart = startOfYear(firstEntryDate);
            }

            while(isBefore(binStart, lastEntryDate)) {
                if (binType === 'week') {
                    nextBinStart = addWeeks(binStart, 1);
                } else if (binType === 'month') {
                    nextBinStart = addMonths(binStart, 1);
                } else {
                    nextBinStart = addYears(binStart, 1);
                }

                // count patients in current bin
                const patientsInBin = groupedData[locationId].filter(entry => 
                    entry.patientStartDates >= binStart && entry.patientStartDates < nextBinStart
                ).length;

                countData.push({
                    locationId: locationId,
                    locationName: groupedData[locationId][0].locationName,
                    date: binStart,
                    newPatients: patientsInBin
                });

                binStart = nextBinStart;
            }
        }
    });
    return countData;
}